// ═══════════════════════════════════════════════════════════════════════════
// HEADER : menu compte + page « Mon compte » + modale changer mdp + saisie
// rapide d'action
// Migration S5.4 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════

import {
  mountCaptureBar,
  type CaptureContext,
  type CaptureUser,
  type CaptureProject,
  type CaptureCommittee,
  type CaptureStructure,
} from './component-action-capture/index.js';
import { buildMyAccountBody, buildAccountMenuHtml } from './header-account-modal.js';
import { USAGE_ROWS } from './page-my-quotas.js';

export interface HdrUser {
  id?: number;
  name?: string;
  trigram?: string | null;
  role?: string;
  is_external?: boolean;
  departure_date?: string | null;
  groups?: Array<{ name: string }>;
  locale?: string;
  team_ids?: number[];
  code_client?: string | null;
  is_evaluation?: boolean;
  application_closure_date?: string | null;
}

interface NotifPrefsResp {
  notif_enabled?: boolean;
  notif_level_accepted?: number;
}

interface IaConsentResp {
  consent?: boolean;
  // 'user' en solo (par utilisateur), 'org' en multi (toute l'organisation).
  scope?: string;
  // L'utilisateur courant peut-il modifier ce consentement ?
  can_edit?: boolean;
  // Provider IA actif du ring ('mistral' UE / 'anthropic' US) : sélectionne le
  // libellé de consentement affiché (transfert hors UE ⇔ Anthropic).
  provider?: string;
}

declare global {
  interface Window {
    closeAccountMenu: typeof closeAccountMenu;
    openChangePasswordModal: typeof openChangePasswordModal;
    POSTIT_COLORS: typeof POSTIT_COLORS;
    __authUser?: HdrUser | null;
  }
}

let _accountMenuEl: HTMLDivElement | null = null;
let _accountMenuOutsideHandler: ((e: MouseEvent) => void) | null = null;
let _accountMenuEscHandler: ((e: KeyboardEvent) => void) | null = null;

function closeAccountMenu(): void {
  if (_accountMenuEl) _accountMenuEl.hidden = true;
  if (_accountMenuOutsideHandler) {
    document.removeEventListener('mousedown', _accountMenuOutsideHandler, true);
    _accountMenuOutsideHandler = null;
  }
  if (_accountMenuEscHandler) {
    document.removeEventListener('keydown', _accountMenuEscHandler);
    _accountMenuEscHandler = null;
  }
}

function toggleAccountMenu(): void {
  if (!_accountMenuEl) {
    _accountMenuEl = document.createElement('div');
    _accountMenuEl.id = 'account-menu';
    _accountMenuEl.className = 'account-menu';
    _accountMenuEl.hidden = true;
    document.body.appendChild(_accountMenuEl);
  }

  if (!_accountMenuEl.hidden) {
    closeAccountMenu();
    return;
  }

  const u: HdrUser = window.__authUser || {};
  const groups = Array.isArray(u.groups) ? u.groups : [];

  const LOCALE_NAMES: Record<string, string> = {
    fr: 'Français',
    en: 'English',
    de: 'Deutsch',
    es: 'Español',
  };

  const renderBody = (teamNames: string[], locales: string[]): void => {
    if (!_accountMenuEl) return;
    const canBugs = !!window.hasPermission?.('bugs.read');
    const bugCount = Number(window.__bugsBadgeCount) || 0;

    // Menu déroulant : rendu pur délégué à header-account-modal.
    _accountMenuEl.innerHTML = buildAccountMenuHtml(u, groups, teamNames, canBugs, bugCount);

    _accountMenuEl.querySelector('#am-my-account')?.addEventListener('click', () => {
      closeAccountMenu();
      window.location.hash = '#my-account';
    });
    // Items déplacés depuis la barre header dans le menu (refonte 2026-07).
    _accountMenuEl.querySelector('#am-assistant')?.addEventListener('click', () => {
      closeAccountMenu();
      window.showAssistant?.();
    });
    _accountMenuEl.querySelector('#am-bug')?.addEventListener('click', () => {
      closeAccountMenu();
      window.location.hash = '#bugs';
    });
    _accountMenuEl.querySelector('#am-about')?.addEventListener('click', () => {
      closeAccountMenu();
      void _showAboutModal();
    });
    // Crédits IA dans la popup compte (porte-monnaie, mai 2026). Affiche le
    // VRAI solde restant (credits_remaining = mensuel + acheté) renvoyé par
    // /me/quotas, et garde le détail Analyse/Génération en statistiques d'usage.
    void (async () => {
      const host = _accountMenuEl?.querySelector('#am-quotas-host') as HTMLElement | null;
      if (!host) return;
      try {
        const q = (await api.myQuotas()) as {
          credits_remaining: number;
          credits_extra: number;
          used: Record<string, number>;
          period_start: string;
          tenant_type: string;
        };
        const remaining = q.credits_remaining || 0;
        const extra = q.credits_extra || 0;
        // Les MÊMES séries que « Mes quotas », importées et non recopiées : une
        // liste figée à `analyze`/`redaction` faisait disparaître la revue du
        // référentiel de ce total, alors que ses crédits étaient bien débités.
        const parts = USAGE_ROWS.map((row) => ({ ...row, used: q.used[row.key] || 0 }));
        const usedTotal = parts.reduce((n, part) => n + part.used, 0);
        const segments = parts
          .map(
            (part) =>
              `<div style="width:${usedTotal > 0 ? (part.used / usedTotal) * 100 : 0}%;background:${part.tone};height:100%" title="${esc(part.label)} : ${part.used}"></div>`,
          )
          .join('');
        const legend = parts
          .map(
            (part) =>
              `<span><span style="display:inline-block;width:8px;height:8px;background:${part.tone};border-radius:2px;margin-right:4px"></span>${esc(part.label)} : ${part.used}</span>`,
          )
          .join('');
        host.innerHTML = `
          <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px">
            <span><strong style="font-size:15px;color:var(--text)">${remaining}</strong> <span style="font-size:11px;color:var(--text3)">crédit${remaining > 1 ? 's' : ''} restant${remaining > 1 ? 's' : ''}</span></span>
            ${extra > 0 ? `<span style="font-size:11px;color:var(--text3)">dont ${extra} acheté${extra > 1 ? 's' : ''}</span>` : ''}
          </div>
          <div style="font-size:11px;color:var(--text3);margin-bottom:4px">Consommé ${q.tenant_type === 'solo' ? 'ce mois' : 'par le tenant'} : ${usedTotal}</div>
          <div style="display:flex;width:100%;height:8px;background:#f1f1f1;border-radius:999px;overflow:hidden">${segments}</div>
          <div style="display:flex;gap:10px;margin-top:6px;font-size:11px;color:var(--text2);flex-wrap:wrap">${legend}</div>
        `;
      } catch {
        host.innerHTML = '<span style="font-style:italic">Indisponible</span>';
      }
    })();
    const logoutBtn = _accountMenuEl.querySelector<HTMLButtonElement>('#am-logout');
    logoutBtn?.addEventListener('click', () => {
      void api.withButton(logoutBtn, async () => {
        try {
          await api.logout();
        } catch (e: unknown) {
          safeWarn('api.logout', e);
        }
        // A1-4.4 (revue 2026-08-21) : la déconnexion ne faisait que recharger
        // la page — l'état d'interface (filtres, tri, volets repliés…) posé
        // en localStorage/sessionStorage survivait, y compris sur un poste
        // partagé. Purge complète : aucun de ces états n'a de valeur au-delà
        // de la session en cours.
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (e: unknown) {
          safeWarn('logout storage clear', e);
        }
        window.__authUser = null;
        window.location.reload();
      });
    });

    if (Array.isArray(locales) && locales.length > 1) {
      const sel = _accountMenuEl.querySelector<HTMLSelectElement>('#am-locale-select');
      const row = _accountMenuEl.querySelector<HTMLElement>('#am-locale-row');
      const lbl = _accountMenuEl.querySelector<HTMLElement>('#am-locale-label');
      if (sel && row && lbl) {
        const currentLocale = u.locale || 'fr';

        sel.innerHTML = locales
          .map((loc) => {
            const label = LOCALE_NAMES[loc] || loc;
            const selected = loc === currentLocale ? ' selected' : '';
            return `<option value="${esc(loc)}"${selected}>${esc(label)}</option>`;
          })
          .join('');
        row.style.display = '';
        lbl.style.display = '';
        sel.addEventListener('change', () => {
          void (async () => {
            const newLocale = sel.value;
            if (newLocale === currentLocale) return;
            sel.disabled = true;
            try {
              await api.setMyLocale(newLocale);
              window.location.reload();
            } catch (err: unknown) {
              sel.disabled = false;
              sel.value = currentLocale;
              const msg = err instanceof Error ? err.message : t('common.error');
              toast(msg, 'error');
            }
          })();
        });
      }
    }
  };

  const btn = document.getElementById('header-account-btn');
  _accountMenuEl.hidden = false;
  if (btn) {
    const r = btn.getBoundingClientRect();
    _accountMenuEl.style.top = `${Math.round(r.bottom + 6)}px`;
    _accountMenuEl.style.left = `${Math.max(8, Math.round(r.right - 260))}px`;
  }

  renderBody([], []);

  if (btn) {
    const r2 = btn.getBoundingClientRect();
    const w = _accountMenuEl.offsetWidth || 280;
    _accountMenuEl.style.left = `${Math.max(8, Math.round(r2.right - w))}px`;
  }

  void (async () => {
    const [teamsRes, localesRes] = await Promise.allSettled([api.getUsers(), api.getI18nLocales()]);

    let teamNames: string[] = [];
    if (teamsRes.status === 'fulfilled') {
      const users = teamsRes.value as HdrUser[];
      const me = users.find((x) => x.id === u.id);
      if (me && me.team_ids && me.team_ids.length) {
        const byId: Record<number, HdrUser> = Object.fromEntries(users.map((x) => [x.id ?? 0, x]));
        teamNames = me.team_ids.map((id) => byId[id]?.name).filter((n): n is string => Boolean(n));
      }
    } else {
      safeWarn('api.getUsers (account menu teams)', teamsRes.reason);
    }

    let locales: string[] = [];
    if (localesRes.status === 'fulfilled') {
      const v = localesRes.value as { locales?: string[] };
      locales = Array.isArray(v?.locales) ? v.locales : [];
    } else {
      safeWarn('api.getI18nLocales (account menu)', localesRes.reason);
    }

    if (_accountMenuEl && !_accountMenuEl.hidden) renderBody(teamNames, locales);
  })();

  setTimeout(() => {
    _accountMenuOutsideHandler = (e: MouseEvent): void => {
      if (!_accountMenuEl) return;
      const target = e.target as Node | null;
      if (target && _accountMenuEl.contains(target)) return;
      const b = document.getElementById('header-account-btn');
      if (b && target && b.contains(target)) return;
      closeAccountMenu();
    };
    document.addEventListener('mousedown', _accountMenuOutsideHandler, true);

    _accountMenuEscHandler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeAccountMenu();
    };
    document.addEventListener('keydown', _accountMenuEscHandler);
  }, 0);
}

// ── Sondage de satisfaction (2e onglet de « Mon compte ») ────────────────────

interface SurveyGauge {
  id: string;
  q: string;
  min: number;
  max: number;
  lo: string;
  hi: string;
}

const _SURVEY_GAUGES: ReadonlyArray<SurveyGauge> = [
  {
    id: 'sv-satisfaction',
    q: 'pageSurvey.q.satisfaction',
    min: 1,
    max: 10,
    lo: 'pageSurvey.satisfaction.min',
    hi: 'pageSurvey.satisfaction.max',
  },
  {
    id: 'sv-nps',
    q: 'pageSurvey.q.nps',
    min: 0,
    max: 10,
    lo: 'pageSurvey.nps.min',
    hi: 'pageSurvey.nps.max',
  },
  {
    id: 'sv-ease',
    q: 'pageSurvey.q.ease',
    min: 1,
    max: 10,
    lo: 'pageSurvey.ease.min',
    hi: 'pageSurvey.ease.max',
  },
  {
    id: 'sv-timesaving',
    q: 'pageSurvey.q.timeSavingScale',
    min: 0,
    max: 10,
    lo: 'pageSurvey.timeSaving.min',
    hi: 'pageSurvey.timeSaving.max',
  },
];

function _injectMyAccountCss(): void {
  if (document.getElementById('ma-modal-css')) return;
  const el = document.createElement('style');
  el.id = 'ma-modal-css';
  el.textContent = `
    .ma-tabs{display:flex;gap:2px;border-bottom:1px solid var(--border);margin:-4px 0 18px}
    .ma-tab{appearance:none;background:none;border:none;padding:10px 16px;font:inherit;font-weight:600;font-size:14px;color:var(--text3);cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px;transition:color .15s,border-color .15s}
    .ma-tab:hover{color:var(--text2)}
    .ma-tab.is-active{color:var(--accent);border-bottom-color:var(--accent)}
    .sv-intro{color:var(--text2);line-height:1.5;margin:0 0 18px;font-size:13px}
    .sv-field{margin-bottom:16px}
    .sv-label{display:block;font-weight:600;font-size:13px;margin-bottom:6px}
    .sv-radios{display:flex;flex-wrap:wrap;gap:8px}
    .sv-radio{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border:1px solid var(--border);border-radius:999px;cursor:pointer;font-size:13px;transition:border-color .15s,background .15s}
    .sv-radio:hover{border-color:var(--accent)}
    .sv-radio:has(input:checked){border-color:var(--accent);background:color-mix(in srgb,var(--accent) 12%,transparent)}
    .sv-gauge{border:1px solid var(--border);border-radius:var(--radius);padding:12px 14px;margin-bottom:14px;background:var(--bg2)}
    .sv-gauge-top{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px}
    .sv-gauge-q{font-weight:600;font-size:13px}
    .sv-gauge-val{min-width:34px;text-align:center;font-weight:700;font-variant-numeric:tabular-nums;background:var(--accent);color:#fff;border-radius:999px;padding:2px 9px;font-size:13px;transition:background .15s}
    .sv-gauge-range{width:100%;accent-color:var(--accent);cursor:pointer;transition:opacity .15s}
    .sv-gauge-scale{display:flex;justify-content:space-between;font-size:11px;color:var(--text3);margin-top:3px}
    .sv-gauge-na{display:inline-flex;align-items:center;gap:6px;font-size:12px;color:var(--text3);margin-top:10px;cursor:pointer}
    .sv-gauge.is-na .sv-gauge-range{opacity:.3}
    .sv-gauge.is-na .sv-gauge-val{background:var(--border);color:var(--text3)}
    .sv-thanks{text-align:center;padding:36px 8px}
    .sv-thanks-check{display:inline-flex;align-items:center;justify-content:center;width:56px;height:56px;border-radius:50%;background:color-mix(in srgb,var(--accent) 15%,transparent);color:var(--accent);font-size:30px;font-weight:700}
    .sv-thanks h3{margin:14px 0 6px}
    .sv-thanks p{color:var(--text2);margin:0;line-height:1.5}
  `;
  document.head.appendChild(el);
}

function _surveyGaugeHtml(g: SurveyGauge): string {
  return `
    <div class="sv-gauge is-na" data-gauge="${g.id}">
      <div class="sv-gauge-top">
        <span class="sv-gauge-q">${esc(t(g.q))}</span>
        <span class="sv-gauge-val" id="${g.id}-val">—</span>
      </div>
      <input type="range" class="sv-gauge-range" id="${g.id}-range" min="${g.min}" max="${g.max}" step="1" value="${g.min}" aria-label="${esc(t(g.q))}">
      <div class="sv-gauge-scale"><span>${esc(t(g.lo))}</span><span>${esc(t(g.hi))}</span></div>
      <label class="sv-gauge-na"><input type="checkbox" id="${g.id}-na" checked> ${esc(t('pageSurvey.notAnswered'))}</label>
    </div>`;
}

function _wireSurveyGauge(id: string): void {
  const wrap = document.querySelector<HTMLElement>(`[data-gauge="${id}"]`);
  const range = document.getElementById(`${id}-range`) as HTMLInputElement | null;
  const na = document.getElementById(`${id}-na`) as HTMLInputElement | null;
  const val = document.getElementById(`${id}-val`);
  if (!wrap || !range || !na || !val) return;
  const refresh = (): void => {
    if (na.checked) {
      wrap.classList.add('is-na');
      val.textContent = '—';
    } else {
      wrap.classList.remove('is-na');
      val.textContent = range.value;
    }
  };
  range.addEventListener('input', () => {
    na.checked = false;
    refresh();
  });
  na.addEventListener('change', refresh);
  refresh();
}

function _readSurveyGauge(id: string): number | null {
  const range = document.getElementById(`${id}-range`) as HTMLInputElement | null;
  const na = document.getElementById(`${id}-na`) as HTMLInputElement | null;
  if (!range || !na || na.checked) return null;
  const n = Number(range.value);
  return Number.isFinite(n) ? n : null;
}

function _surveyTabHtml(): string {
  const ta =
    'width:100%;min-height:60px;padding:8px 10px;border:1px solid var(--border);border-radius:var(--radius);font:inherit;resize:vertical;box-sizing:border-box';
  const radios = (name: string, opts: ReadonlyArray<readonly [string, string]>): string =>
    opts
      .map(
        ([v, k]) =>
          `<label class="sv-radio"><input type="radio" name="${name}" value="${v}"> ${esc(t(k))}</label>`,
      )
      .join('');
  const adoption = radios('sv-adoption', [
    ['oui', 'pageSurvey.adoption.oui'],
    ['probable', 'pageSurvey.adoption.probable'],
    ['indecis', 'pageSurvey.adoption.indecis'],
    ['non', 'pageSurvey.adoption.non'],
  ]);
  const recontact = radios('sv-recontact', [
    ['true', 'common.yes'],
    ['false', 'common.no'],
  ]);
  return `
    <div id="sv-form">
      <p class="sv-intro">${esc(t('pageSurvey.intro'))}</p>
      ${_SURVEY_GAUGES.slice(0, 3).map(_surveyGaugeHtml).join('')}
      <div class="sv-field"><label class="sv-label">${esc(t('pageSurvey.q.favorite'))}</label><textarea id="sv-favorite" maxlength="2000" style="${ta}"></textarea></div>
      <div class="sv-field"><label class="sv-label">${esc(t('pageSurvey.q.frustrating'))}</label><textarea id="sv-frustrating" maxlength="2000" style="${ta}"></textarea></div>
      <div class="sv-field"><label class="sv-label">${esc(t('pageSurvey.q.ideas'))}</label><textarea id="sv-ideas" maxlength="2000" style="${ta}"></textarea></div>
      ${_surveyGaugeHtml(_SURVEY_GAUGES[3] as SurveyGauge)}
      <div class="sv-field"><label class="sv-label">${esc(t('pageSurvey.q.timeSavingHours'))}</label><div><input type="number" id="sv-hours" min="0" max="168" step="0.5" placeholder="${esc(t('pageSurvey.notAnswered'))}" style="width:130px;padding:8px 10px;border:1px solid var(--border);border-radius:var(--radius);font:inherit"><span style="color:var(--text3);font-size:12px;margin-left:8px">${esc(t('pageSurvey.hoursUnit'))}</span></div></div>
      <div class="sv-field"><label class="sv-label">${esc(t('pageSurvey.q.adoption'))}</label><div class="sv-radios">${adoption}</div></div>
      <div class="sv-field"><label class="sv-label">${esc(t('pageSurvey.q.recontact'))}</label><div class="sv-radios">${recontact}</div></div>
      <div class="sv-field"><label class="sv-label">${esc(t('pageSurvey.q.comment'))}</label><textarea id="sv-comment" maxlength="4000" style="${ta}"></textarea></div>
      <div id="sv-err" style="color:var(--danger);font-size:13px;min-height:18px;margin-bottom:6px"></div>
      <div style="text-align:right"><button type="button" class="btn btn-primary" id="sv-submit">${esc(t('pageSurvey.btnSubmit'))}</button></div>
    </div>`;
}

function _wireSurveyTab(): void {
  ['sv-satisfaction', 'sv-nps', 'sv-ease', 'sv-timesaving'].forEach(_wireSurveyGauge);
  const btn = document.getElementById('sv-submit') as HTMLButtonElement | null;
  if (!btn) return;
  const readText = (id: string): string | null => {
    const el = document.getElementById(id) as HTMLTextAreaElement | null;
    const v = (el?.value || '').trim();
    return v || null;
  };
  const radio = (name: string): string | null => {
    const el = document.querySelector<HTMLInputElement>(`input[name="${name}"]:checked`);
    return el ? el.value : null;
  };
  btn.addEventListener('click', () => {
    const err = document.getElementById('sv-err');
    if (err) err.textContent = '';
    const hoursEl = document.getElementById('sv-hours') as HTMLInputElement | null;
    const raw = (hoursEl?.value || '').trim();
    let hours: number | null = null;
    if (raw !== '') {
      const h = Number(raw.replace(',', '.'));
      if (!Number.isFinite(h) || h < 0 || h > 168) {
        if (err) err.textContent = t('pageSurvey.errHours');
        return;
      }
      hours = Math.round(h * 100) / 100;
    }
    const rec = radio('sv-recontact');
    const payload = {
      satisfaction: _readSurveyGauge('sv-satisfaction'),
      nps: _readSurveyGauge('sv-nps'),
      ease_of_use: _readSurveyGauge('sv-ease'),
      favorite_features: readText('sv-favorite'),
      frustrating_features: readText('sv-frustrating'),
      improvement_ideas: readText('sv-ideas'),
      time_saving_scale: _readSurveyGauge('sv-timesaving'),
      time_saving_hours: hours,
      adoption_intent: radio('sv-adoption'),
      allow_recontact: rec === null ? null : rec === 'true',
      free_comment: readText('sv-comment'),
    };
    void api.withButton(btn, async () => {
      try {
        await api.submitSurvey(payload);
        const form = document.getElementById('sv-form');
        if (form) {
          form.innerHTML = `<div class="sv-thanks"><div class="sv-thanks-check">✓</div><h3>${esc(t('pageSurvey.thanksTitle'))}</h3><p>${esc(t('pageSurvey.thanksBody'))}</p></div>`;
        }
      } catch (ex: unknown) {
        const msg = ex instanceof Error ? ex.message : t('common.error');
        if (err) err.textContent = msg;
      }
    });
  });
}

/** Rend « Mon compte » dans la zone principale (page `#my-account`).
 *
 * Était une modale jusqu'au 2026-08-24. Passée en page pour que le réglage des
 * relances ait une URL : le pied des mails groupés doit pouvoir y renvoyer
 * (cf. `docs/MAILS.md` § 1.4). Le corps reste rendu par `buildMyAccountBody`,
 * le câblage est identique — seul le conteneur change.
 */
export async function renderMyAccountPage(): Promise<void> {
  setHeader(t('myAccount.title'), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  let prefs: NotifPrefsResp;
  let consent: IaConsentResp;
  try {
    prefs = (await api.getMyNotifPrefs()) as NotifPrefsResp;
    consent = (await api.getMyIaConsent()) as IaConsentResp;
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error" style="padding:16px">${esc(msg)}</div>`);
    return;
  }
  const consentChecked = !!consent.consent;
  const consentEditable = !!consent.can_edit;
  const consentIsOrg = consent.scope === 'org';
  // Provider actif : 'mistral' (UE) par défaut si le backend ne le renvoie pas
  // (compat descendante), sinon la valeur reçue. Sélectionne le libellé i18n.
  const consentProvider = consent.provider === 'anthropic' ? 'anthropic' : 'mistral';

  // Achat de crédits IA (popup Mon compte) — visible si le droit est accordé.
  const canBuyCredits = !!(window.hasPermission && window.hasPermission('ia_credits.buy'));
  let purchaseStep = 0;
  if (canBuyCredits) {
    try {
      const q = (await api.myQuotas()) as { purchase_step?: number };
      purchaseStep = Number(q.purchase_step) || 0;
    } catch {
      /* best-effort : ne pas bloquer la popup si /quotas échoue */
    }
  }
  let committeeAlertsEnabled = true;
  try {
    const caPrefs = (await api.getMyCommitteeAlertPrefs()) as { enabled?: boolean };
    committeeAlertsEnabled = caPrefs.enabled !== false;
  } catch {
    /* best-effort : ne pas bloquer la popup Mon compte */
  }
  let mascotEnabled = true;
  try {
    const mPrefs = (await api.getMyMascotPrefs()) as { enabled?: boolean };
    mascotEnabled = mPrefs.enabled !== false;
  } catch {
    /* best-effort : ne pas bloquer la popup Mon compte */
  }
  const enabled = !!prefs.notif_enabled;
  const lvl = Number.isInteger(prefs.notif_level_accepted) ? (prefs.notif_level_accepted ?? 2) : 2;
  // Corps de la popup : rendu pur délégué à header-account-modal (entrées
  // immuables passées explicitement ; onglet sondage rendu ici et threadé).
  const body = buildMyAccountBody({
    consentChecked,
    consentEditable,
    consentIsOrg,
    consentProvider,
    canBuyCredits,
    purchaseStep,
    enabled,
    lvl,
    committeeAlertsEnabled,
    mascotEnabled,
    surveyTabHtml: _surveyTabHtml(),
  });
  _injectMyAccountCss();
  setContent(`<div class="card" style="max-width:760px">${body}</div>`);

  const _tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.ma-tab'));
  const _panes = Array.from(document.querySelectorAll<HTMLElement>('.ma-pane'));
  _tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const name = tab.dataset['tab'];
      _tabs.forEach((other) => other.classList.toggle('is-active', other === tab));
      _panes.forEach((pane) => {
        pane.hidden = pane.dataset['pane'] !== name;
      });
    });
  });
  _wireSurveyTab();

  document.getElementById('ma-notif-save')?.addEventListener('click', () => {
    const btn = document.getElementById('ma-notif-save') as HTMLButtonElement | null;
    const enabledEl = document.getElementById('ma-notif-enabled') as HTMLInputElement | null;
    const lvlEl = document.getElementById('ma-notif-level') as HTMLSelectElement | null;
    const newEnabled = !!enabledEl?.checked;
    const newLvl = parseInt(lvlEl?.value || '0', 10);
    const err = document.getElementById('ma-notif-err');
    if (err) err.textContent = '';
    if (!Number.isInteger(newLvl) || newLvl < 0 || newLvl > 4) {
      if (err) err.textContent = t('myAccount.notif.errLevel');
      return;
    }
    if (!btn) return;
    void api.withButton(btn, async () => {
      try {
        await api.patchMyNotifPrefs({ notif_enabled: newEnabled, notif_level_accepted: newLvl });
        toast(t('myAccount.toast'));
        if (typeof window.refreshNotifBadge === 'function') window.refreshNotifBadge();
      } catch (ex: unknown) {
        const msg = ex instanceof Error ? ex.message : t('common.error');
        if (err) err.textContent = msg;
      }
    });
  });

  document.getElementById('ma-committee-alerts-save')?.addEventListener('click', () => {
    const btn = document.getElementById('ma-committee-alerts-save') as HTMLButtonElement | null;
    const cbEl = document.getElementById('ma-committee-alerts-enabled') as HTMLInputElement | null;
    const err = document.getElementById('ma-committee-alerts-err');
    if (err) err.textContent = '';
    if (!btn || !cbEl) return;
    void api.withButton(btn, async () => {
      try {
        await api.patchMyCommitteeAlertPrefs({ enabled: !!cbEl.checked });
        toast(t('myAccount.toast'));
      } catch (ex: unknown) {
        const msg = ex instanceof Error ? ex.message : t('common.error');
        if (err) err.textContent = msg;
      }
    });
  });

  document.getElementById('ma-mascot-save')?.addEventListener('click', () => {
    const btn = document.getElementById('ma-mascot-save') as HTMLButtonElement | null;
    const cbEl = document.getElementById('ma-mascot-enabled') as HTMLInputElement | null;
    const err = document.getElementById('ma-mascot-err');
    if (err) err.textContent = '';
    if (!btn || !cbEl) return;
    void api.withButton(btn, async () => {
      try {
        await api.patchMyMascotPrefs({ enabled: !!cbEl.checked });
        window.OrnaxionMascot?.invalidateCache();
        toast(t('myAccount.toast'));
      } catch (ex: unknown) {
        const msg = ex instanceof Error ? ex.message : t('common.error');
        if (err) err.textContent = msg;
      }
    });
  });

  document.getElementById('ma-ia-consent-save')?.addEventListener('click', () => {
    const btn = document.getElementById('ma-ia-consent-save') as HTMLButtonElement | null;
    const cbEl = document.getElementById('ma-ia-consent') as HTMLInputElement | null;
    const err = document.getElementById('ma-ia-consent-err');
    if (err) err.textContent = '';
    if (!btn || !cbEl) return;
    void api.withButton(btn, async () => {
      try {
        await api.putMyIaConsent(!!cbEl.checked);
        toast(t('myAccount.iaConsent.toastSaved'));
      } catch (ex: unknown) {
        const msg = ex instanceof Error ? ex.message : t('common.error');
        if (err) err.textContent = msg;
      }
    });
  });

  document.getElementById('ma-ia-credits-buy')?.addEventListener('click', () => {
    openModal(
      t('myAccount.section.iaCredits'),
      `<p>${esc(t('myAccount.iaCredits.confirm', { step: purchaseStep }))}</p>`,
      async () => {
        try {
          await api.buyIaCredits();
          closeModal();
          toast(t('myAccount.iaCredits.toastBought'));
        } catch (ex: unknown) {
          toast(ex instanceof Error ? ex.message : t('common.error'), 'error');
        }
      },
      t('myAccount.iaCredits.confirmBtn'),
    );
  });

  document.getElementById('ma-password-btn')?.addEventListener('click', () => {
    openChangePasswordModal();
  });
}

function openChangePasswordModal(): void {
  const body = `
    <div class="form-group">
      <label>${esc(t('header.password.old'))}</label>
      <input type="password" id="cp-old" autocomplete="current-password" autofocus>
    </div>
    <div class="form-group" style="margin-top:10px">
      <label>${esc(t('header.password.new'))} <span style="color:var(--text3);font-weight:400">${esc(t('header.password.newHint'))}</span></label>
      <input type="password" id="cp-new" autocomplete="new-password">
    </div>
    <div class="form-group" style="margin-top:10px">
      <label>${esc(t('header.password.confirm'))}</label>
      <input type="password" id="cp-confirm" autocomplete="new-password">
    </div>
    <div id="cp-err" style="color:var(--danger);font-size:12px;min-height:16px;margin-top:6px"></div>
  `;
  openModal(
    t('header.password.title'),
    body,
    async () => {
      const oldEl = document.getElementById('cp-old') as HTMLInputElement | null;
      const newEl = document.getElementById('cp-new') as HTMLInputElement | null;
      const confEl = document.getElementById('cp-confirm') as HTMLInputElement | null;
      const oldP = oldEl?.value || '';
      const newP = newEl?.value || '';
      const conf = confEl?.value || '';
      const err = document.getElementById('cp-err');
      if (err) err.textContent = '';
      if (!oldP || !newP) {
        if (err) err.textContent = t('header.password.errAll');
        return;
      }
      if (newP.length < 8) {
        if (err) err.textContent = t('header.password.errMin');
        return;
      }
      if (newP !== conf) {
        if (err) err.textContent = t('header.password.errMatch');
        return;
      }
      try {
        await api.changePassword({ old_password: oldP, new_password: newP });
        closeModal();
        toast(t('header.password.toast'));
      } catch (ex: unknown) {
        const msg = ex instanceof Error ? ex.message : t('common.error');
        if (err) err.textContent = msg;
      }
    },
    t('header.password.btn'),
  );
}

const POSTIT_COLORS: ReadonlyArray<string> = ['yellow', 'orange', 'pink', 'blue', 'green'];

// Monté une seule fois (le header persiste ; boot + auth:ready rappellent).
let _acapHeaderMounted = false;

// Référentiels de la barre : le composant les relit à CHAQUE ouverture de
// typeahead, donc les rafraîchir revient à muter cet objet en place.
let _acapCtx: CaptureContext | null = null;
let _acapRefreshing = false;

// La barre du header n'étant montée qu'une fois par session, ses référentiels
// se périmaient jusqu'au prochain rechargement complet : un projet créé dans la
// session n'apparaissait pas derrière `#`. On les recharge à la prise de focus
// (moment naturel, hors du chemin de frappe), au plus une fois toutes les 30 s.
const ACAP_REFRESH_TTL_MS = 30_000;
let _acapRefreshedAt = 0;

async function refreshHeaderCaptureRefs(input: HTMLInputElement): Promise<void> {
  const ctx = _acapCtx;
  if (!ctx || _acapRefreshing) return;
  if (Date.now() - _acapRefreshedAt < ACAP_REFRESH_TTL_MS) return;
  _acapRefreshing = true;
  try {
    const [usersRaw, projectsRaw, committeesRaw, structuresRaw] = await Promise.all([
      loadUsers(true),
      api.getProjects(),
      loadCommittees(true).catch(() => ctx.committees),
      api.getStructuresForFilter().catch(() => ctx.structures),
    ]);
    const before = ctx.projects.length + ctx.committees.length + ctx.structures.length;
    ctx.users = usersRaw as unknown as CaptureUser[];
    ctx.projects = projectsRaw as CaptureProject[];
    ctx.committees = committeesRaw as unknown as CaptureCommittee[];
    ctx.structures = (structuresRaw as CaptureStructure[]).filter((s) => s.is_active !== false);
    _acapRefreshedAt = Date.now();
    // Le typeahead a pu s'ouvrir pendant le fetch (l'utilisateur saisit `#`
    // aussitôt le focus pris) : le relancer sur les données fraîches.
    const after = ctx.projects.length + ctx.committees.length + ctx.structures.length;
    if (after !== before && document.querySelector('.acap-pop')) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  } catch (ex: unknown) {
    // Référentiels périmés = dégradation acceptable, la barre reste utilisable.
    safeWarn('refreshHeaderCaptureRefs', ex);
  } finally {
    _acapRefreshing = false;
  }
}

// Remplace l'ancien post-it inline par la saisie rapide d'action (barre 0.A,
// mode compact). Les référentiels sont chargés à la volée au 1er montage.
async function renderHeaderActionCapture(): Promise<void> {
  const wrap = document.getElementById('header-quick-action');
  if (!wrap) return;

  if (!(window.hasPermission && window.hasPermission('actions.write'))) {
    wrap.classList.add('hidden');
    wrap.innerHTML = '';
    return;
  }
  wrap.classList.remove('hidden');
  if (_acapHeaderMounted) return;
  _acapHeaderMounted = true;

  try {
    const [usersRaw, projectsRaw, committeesRaw, structuresRaw] = await Promise.all([
      loadUsers(),
      api.getProjects(),
      loadCommittees().catch(() => []),
      api.getStructuresForFilter().catch(() => []),
    ]);
    await loadActionStatuses(); // peuple getDefaultStatusId()
    const structures = (structuresRaw as CaptureStructure[]).filter((s) => s.is_active !== false);
    wrap.innerHTML = '';
    _acapRefreshedAt = Date.now();
    _acapCtx = {
      users: usersRaw as unknown as CaptureUser[],
      projects: projectsRaw as CaptureProject[],
      committees: committeesRaw as unknown as CaptureCommittee[],
      structures,
      defaultStatusId: getDefaultStatusId(),
      onCreated: () => {
        if ((window.location.hash || '').startsWith('#actions')) router.dispatch();
      },
      compact: true,
    };
    mountCaptureBar(wrap, _acapCtx);
    const acapInput = wrap.querySelector<HTMLInputElement>('.acap-input');
    if (acapInput) {
      acapInput.addEventListener('focus', () => void refreshHeaderCaptureRefs(acapInput));
    }
  } catch (ex: unknown) {
    _acapHeaderMounted = false;
    safeWarn('renderHeaderActionCapture', ex);
  }
}

async function _showAboutModal(): Promise<void> {
  openWideModal(
    t('header.about.title'),
    `<div style="padding:16px;color:var(--text3)">${esc(t('common.loading'))}</div>`,
    closeModal,
    t('header.about.btnClose'),
    true,
  );

  let version = 'dev';
  let changelogHtml = '';
  // allSettled (pas all) : un échec du changelog ne doit pas masquer la
  // version (et inversement). Avant, Promise.all rejetait en bloc et la
  // modale affichait "Version dev" alors que /version répondait bien.
  const [vRes, cRes] = await Promise.allSettled([api.getVersion(), api.getChangelog()]);
  if (vRes.status === 'fulfilled') {
    version = (vRes.value as { version?: string })?.version || 'dev';
  } else {
    safeWarn('about modal version', vRes.reason);
  }
  if (cRes.status === 'fulfilled') {
    changelogHtml = (cRes.value as { html?: string })?.html || '';
  } else {
    safeWarn('about modal changelog', cRes.reason);
  }

  const body = document.getElementById('modal-body');
  if (!body) return;

  body.innerHTML = `
    <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">
      <img src="/static/ornexia-icon.png" alt="Ornexia" style="width:32px;height:32px">
      <div>
        <div style="font-size:14px;font-weight:600">Ornexia</div>
        <div style="font-size:12px;color:var(--text3)">${t('header.about.version', { version })}</div>
      </div>
    </div>
    <hr style="border:none;border-top:1px solid var(--border);margin:8px 0 12px 0">
    <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--text3);margin-bottom:6px">${esc(t('header.about.changelog'))}</div>
    <div class="changelog-content" style="font-size:13px;line-height:1.5">
      ${changelogHtml || `<div style="color:var(--text3);font-style:italic">${esc(t('header.about.empty'))}</div>`}
    </div>
  `;
}

// ── ESM globals shim (lot B.8 + S5.4) ──
window.closeAccountMenu = closeAccountMenu;
window.openChangePasswordModal = openChangePasswordModal;
window.POSTIT_COLORS = POSTIT_COLORS;
// toggleAccountMenu, renderHeaderActionCapture, _showAboutModal sont typés via
// core-globals.d.ts (déclarations bare-ref).
(globalThis as { toggleAccountMenu?: () => void }).toggleAccountMenu = toggleAccountMenu;
(globalThis as { renderHeaderActionCapture?: () => void }).renderHeaderActionCapture =
  renderHeaderActionCapture;
(globalThis as { _showAboutModal?: () => void | Promise<void> })._showAboutModal = _showAboutModal;

export {};
